// ============================================================================
// Nu-Link Protocol — Packet Builder & Response Parser
// ============================================================================
//
// Supports protocol v1 (8-bit length) and v2 (16-bit length).
// Exact byte layout must be verified with Wireshark captures from
// the official Nuvoton NuMicro ICP Programming Tool.

import { NuLinkCmd, NULINK_REPORT_SIZE } from './constants';

/**
 * Build a Nu-Link command packet.
 *
 * v1 packet layout (64 bytes):
 *   [0]    Packet type (0x01=version, 0x02=command)
 *   [1]    Payload length (8-bit)
 *   [2]    Command opcode
 *   [3]    Sub-command / flags
 *   [4-7]  Parameter 1 (LE 32-bit)
 *   [8-11] Parameter 2 (LE 32-bit)
 *   [12-15] Parameter 3 (LE 32-bit)
 *   [16+]  Data payload
 *
 * v2 packet layout (64 bytes):
 *   [0]    Packet type
 *   [1-2]  Payload length (LE 16-bit)
 *   [3]    Command opcode
 *   ... (similar from offset 4)
 */
export function buildPacket(
  cmd: NuLinkCmd,
  params: {
    subCmd?: number;
    param1?: number;
    param2?: number;
    param3?: number;
    data?: Uint8Array;
  } = {},
  protocolVersion: 1 | 2 = 1
): Uint8Array {
  const packet = new Uint8Array(NULINK_REPORT_SIZE);
  const view = new DataView(packet.buffer);

  if (protocolVersion === 1) {
    // Calculate payload length (everything after the length byte)
    const dataLen = params.data?.length ?? 0;
    const payloadLen = 14 + dataLen; // cmd(1) + sub(1) + 3*param(12) + data

    packet[0] = cmd === NuLinkCmd.GET_VERSION ? 0x01 : 0x02;
    packet[1] = Math.min(payloadLen, 62);  // Max 62 bytes payload in 64-byte report
    packet[2] = cmd;
    packet[3] = params.subCmd ?? 0;
    view.setUint32(4, params.param1 ?? 0, true);   // LE
    view.setUint32(8, params.param2 ?? 0, true);    // LE
    view.setUint32(12, params.param3 ?? 0, true);   // LE

    if (params.data && params.data.length > 0) {
      const maxData = NULINK_REPORT_SIZE - 16;
      packet.set(params.data.subarray(0, maxData), 16);
    }
  } else {
    // Protocol v2: 16-bit length field
    const dataLen = params.data?.length ?? 0;
    const payloadLen = 13 + dataLen;

    packet[0] = cmd === NuLinkCmd.GET_VERSION ? 0x01 : 0x02;
    view.setUint16(1, payloadLen, true);  // LE 16-bit length
    packet[3] = cmd;
    packet[4] = params.subCmd ?? 0;
    view.setUint32(5, params.param1 ?? 0, true);
    view.setUint32(9, params.param2 ?? 0, true);
    view.setUint32(13, params.param3 ?? 0, true);

    if (params.data && params.data.length > 0) {
      const maxData = NULINK_REPORT_SIZE - 17;
      packet.set(params.data.subarray(0, maxData), 17);
    }
  }

  return packet;
}

/**
 * Parse a Nu-Link response packet.
 * Returns the command-specific data fields.
 */
export function parseResponse(
  data: Uint8Array,
  _cmd: NuLinkCmd,
  protocolVersion: 1 | 2 = 1
): { status: number; payload: Uint8Array; param1: number; param2: number } {
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);

  if (protocolVersion === 1) {
    return {
      status: data[0],
      payload: data.subarray(16),
      param1: view.getUint32(4, true),
      param2: view.getUint32(8, true),
    };
  } else {
    return {
      status: data[0],
      payload: data.subarray(17),
      param1: view.getUint32(5, true),
      param2: view.getUint32(9, true),
    };
  }
}
